/**
 * BookingWidgetView — public, embeddable room-booking widget.
 *
 * Served at /book/<widget_token> with no auth. Dates + guests first (one
 * shared calendar), then room results, then a focused guest-details step.
 */
import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  DEFAULT_PHONE_COUNTRY_ISO,
  composeBookingPhone,
  isValidGuestEmail,
  type PhoneCountryIso,
} from '@cafe-pos/shared';
import { PhoneCountryInput } from '../PhoneCountryInput.js';

interface WidgetRoom {
  id: string;
  name: string;
  capacity: number;
  price_amd: number;
  photos: string[];
}

interface WidgetBusy {
  room_id: string;
  check_in: string;
  check_out: string;
}

interface WidgetData {
  name: string;
  accent?: string;
  rooms: WidgetRoom[];
  busy: WidgetBusy[];
}

type Step = 'browse' | 'book' | 'success';

const FN_BASE = `${(import.meta.env.VITE_SUPABASE_URL as string | undefined) ?? ''}/functions/v1/booking-widget`;
const MAX_STAY_NIGHTS = 60;
const LOOKAHEAD_MONTHS = 6;
const DEFAULT_ACCENT = '#0e7490';
const HEX_RE = /^#[0-9A-Fa-f]{6}$/;

function isoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseIso(iso: string): Date {
  return new Date(iso + 'T00:00:00');
}

function addDaysIso(iso: string, days: number): string {
  const d = parseIso(iso);
  d.setDate(d.getDate() + days);
  return isoDate(d);
}

function nightsBetween(from: string, to: string): number {
  return Math.round((Date.parse(to) - Date.parse(from)) / 86_400_000);
}

function shiftMonth(year: number, month: number, delta: number): { year: number; month: number } {
  const d = new Date(year, month + delta, 1);
  return { year: d.getFullYear(), month: d.getMonth() };
}

export function BookingWidgetView({ token }: { token: string }) {
  const { t, i18n } = useTranslation();
  const today = isoDate(new Date());
  const rangeFrom = isoDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const rangeTo = isoDate(
    new Date(new Date().getFullYear(), new Date().getMonth() + LOOKAHEAD_MONTHS, 1),
  );

  const [data, setData] = useState<WidgetData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [checkIn, setCheckIn] = useState(today);
  const [checkOut, setCheckOut] = useState(() => addDaysIso(today, 1));
  const [guests, setGuests] = useState(1);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [picking, setPicking] = useState<'in' | 'out'>('in');
  const [pickerMonth, setPickerMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });
  const [photoIdx, setPhotoIdx] = useState<Record<string, number>>({});
  const [step, setStep] = useState<Step>('browse');
  const [selectedRoomId, setSelectedRoomId] = useState<string | null>(null);
  const [guestName, setGuestName] = useState('');
  const [phoneIso, setPhoneIso] = useState<PhoneCountryIso>(DEFAULT_PHONE_COUNTRY_ISO);
  const [phone, setPhone] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<'conflict' | 'error' | null>(null);
  const [bannerError, setBannerError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [gallery, setGallery] = useState<{ roomId: string; index: number } | null>(null);
  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const lang = new URLSearchParams(window.location.search).get('lang');
    if (lang === 'hy' || lang === 'en') void i18n.changeLanguage(lang);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function fetchData() {
      setLoading(true);
      setError(false);
      const params = new URLSearchParams({ token, from: rangeFrom, to: rangeTo });
      try {
        const res = await fetch(`${FN_BASE}?${params}`, { cache: 'no-store' });
        if (!res.ok) throw new Error(String(res.status));
        const body = (await res.json()) as WidgetData;
        if (!cancelled) setData(body);
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void fetchData();
    return () => {
      cancelled = true;
    };
  }, [token, rangeFrom, rangeTo, reloadKey]);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!pickerOpen) return;
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setPickerOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setPickerOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [pickerOpen]);

  const locale = i18n.language === 'hy' ? 'hy-AM' : 'en-US';
  const stayNights = checkIn && checkOut && checkOut > checkIn ? nightsBetween(checkIn, checkOut) : 0;
  const datesReady = stayNights >= 1;

  const busyNights = useMemo(() => {
    const map = new Map<string, Set<string>>();
    if (!data) return map;
    for (const b of data.busy) {
      const set = map.get(b.room_id) ?? new Set<string>();
      const start = parseIso(b.check_in);
      const end = parseIso(b.check_out);
      for (let d = new Date(start); d < end; d.setDate(d.getDate() + 1)) {
        set.add(isoDate(d));
      }
      map.set(b.room_id, set);
    }
    return map;
  }, [data]);

  function rangeFree(roomId: string, from: string, to: string): boolean {
    const set = busyNights.get(roomId);
    if (!set) return true;
    const start = parseIso(from);
    const end = parseIso(to);
    for (let d = new Date(start); d < end; d.setDate(d.getDate() + 1)) {
      if (set.has(isoDate(d))) return false;
    }
    return true;
  }

  function roomFits(room: WidgetRoom): boolean {
    return room.capacity >= guests;
  }

  function roomAvailable(room: WidgetRoom): boolean {
    return datesReady && roomFits(room) && rangeFree(room.id, checkIn, checkOut);
  }

  function canCheckInOn(iso: string): boolean {
    if (iso < today || !data) return iso >= today;
    const next = addDaysIso(iso, 1);
    return data.rooms.some((r) => roomFits(r) && rangeFree(r.id, iso, next));
  }

  function canCheckOutOn(iso: string): boolean {
    if (!checkIn || iso <= checkIn) return false;
    const nights = nightsBetween(checkIn, iso);
    if (nights < 1 || nights > MAX_STAY_NIGHTS) return false;
    if (!data) return true;
    return data.rooms.some((r) => roomFits(r) && rangeFree(r.id, checkIn, iso));
  }

  const maxGuests = useMemo(() => {
    if (!data || data.rooms.length === 0) return 8;
    return Math.max(1, ...data.rooms.map((r) => r.capacity));
  }, [data]);

  const rooms = data?.rooms ?? [];
  const availableRooms = datesReady ? rooms.filter(roomAvailable) : rooms;
  const unavailableRooms = datesReady ? rooms.filter((r) => !roomAvailable(r)) : [];
  const selectedRoom = rooms.find((r) => r.id === selectedRoomId) ?? null;

  const weekdayNames = useMemo(() => {
    const names: string[] = [];
    for (let i = 0; i < 7; i++) {
      names.push(new Date(2024, 0, 1 + i).toLocaleDateString(locale, { weekday: 'short' }));
    }
    return names;
  }, [locale]);

  const lastMonth = useMemo(() => {
    const d = parseIso(rangeTo);
    d.setDate(d.getDate() - 1);
    return { year: d.getFullYear(), month: d.getMonth() };
  }, [rangeTo]);

  const monthIndex = (year: number, month: number) => year * 12 + month;
  const isCurrentPickerMonth =
    pickerMonth.year === new Date().getFullYear() && pickerMonth.month === new Date().getMonth();
  const isLastPickerMonth =
    monthIndex(pickerMonth.year, pickerMonth.month) >= monthIndex(lastMonth.year, lastMonth.month);
  const secondMonth = shiftMonth(pickerMonth.year, pickerMonth.month, 1);
  const showSecondMonth =
    monthIndex(secondMonth.year, secondMonth.month) <= monthIndex(lastMonth.year, lastMonth.month);

  const AMD = (n: number) => `${Math.round(n).toLocaleString('en-US')} ֏`;

  function formatShort(iso: string): string {
    return parseIso(iso).toLocaleDateString(locale, { day: 'numeric', month: 'short' });
  }

  function formatLong(iso: string): string {
    return parseIso(iso).toLocaleDateString(locale, {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });
  }

  function openPicker(which: 'in' | 'out') {
    setPicking(which);
    setPickerOpen(true);
    const anchor = which === 'out' && checkIn ? parseIso(checkIn) : parseIso(checkIn || today);
    setPickerMonth({ year: anchor.getFullYear(), month: anchor.getMonth() });
  }

  function handleDayClick(iso: string) {
    if (iso < today) return;

    if (picking === 'out' && checkIn && iso > checkIn) {
      if (!canCheckOutOn(iso)) return;
      setCheckOut(iso);
      setPickerOpen(false);
      setBannerError(null);
      return;
    }

    if (!canCheckInOn(iso)) return;
    setCheckIn(iso);
    setCheckOut(addDaysIso(iso, 1));
    setPicking('out');
    setBannerError(null);
  }

  function clearDates() {
    setCheckIn(today);
    setCheckOut(addDaysIso(today, 1));
    setPicking('in');
    setBannerError(null);
  }

  function movePhoto(roomId: string, count: number, delta: number) {
    setPhotoIdx((s) => {
      const cur = s[roomId] ?? 0;
      return { ...s, [roomId]: (cur + delta + count) % count };
    });
  }

  function startBooking(room: WidgetRoom) {
    if (!datesReady || !roomAvailable(room)) {
      openPicker('in');
      return;
    }
    setSelectedRoomId(room.id);
    setSubmitError(null);
    setStep('book');
    setPickerOpen(false);
  }

  function backToBrowse() {
    setStep('browse');
    setSelectedRoomId(null);
    setSubmitError(null);
  }

  async function submitBooking() {
    if (!selectedRoom || !datesReady || submitting) return;
    const name = guestName.trim();
    const email = guestEmail.trim();
    const composed = composeBookingPhone(phoneIso, phone);
    if (!name || composed.replace(/\D/g, '').length < 6 || !isValidGuestEmail(email)) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await fetch(FN_BASE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          room_id: selectedRoom.id,
          check_in: checkIn,
          check_out: checkOut,
          guest_name: name,
          phone: composed,
          email,
          adults: guests,
          lang: i18n.language === 'hy' ? 'hy' : 'en',
        }),
      });
      if (res.status === 409) {
        setSubmitError('conflict');
        setReloadKey((k) => k + 1);
        setStep('browse');
        setSelectedRoomId(null);
        setBannerError(t('bookingWidget.bookConflict'));
        return;
      }
      if (!res.ok) {
        setSubmitError('error');
        return;
      }
      setStep('success');
      setGuestName('');
      setPhone('');
      setPhoneIso(DEFAULT_PHONE_COUNTRY_ISO);
      setGuestEmail('');
      setReloadKey((k) => k + 1);
    } catch {
      setSubmitError('error');
    } finally {
      setSubmitting(false);
    }
  }

  function bookAnother() {
    setStep('browse');
    setSelectedRoomId(null);
    setSubmitError(null);
    setBannerError(null);
  }

  useEffect(() => {
    if (step !== 'book' || !selectedRoom || !datesReady) return;
    if (!rangeFree(selectedRoom.id, checkIn, checkOut) || selectedRoom.capacity < guests) {
      setStep('browse');
      setSelectedRoomId(null);
      setBannerError(t('bookingWidget.datesNoLongerAvailable'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkIn, checkOut, guests, step, selectedRoomId]);

  const formValid =
    guestName.trim().length > 0 &&
    composeBookingPhone(phoneIso, phone).replace(/\D/g, '').length >= 6 &&
    isValidGuestEmail(guestEmail);
  const stayTotal = selectedRoom && selectedRoom.price_amd > 0 ? selectedRoom.price_amd * stayNights : 0;
  const accent = data?.accent && HEX_RE.test(data.accent) ? data.accent.toLowerCase() : DEFAULT_ACCENT;
  const galleryRoom = gallery ? (rooms.find((r) => r.id === gallery.roomId) ?? null) : null;

  function openGallery(roomId: string, index: number) {
    setGallery({ roomId, index });
  }

  function setGalleryIndex(index: number) {
    if (!gallery) return;
    setGallery({ roomId: gallery.roomId, index });
    setPhotoIdx((s) => ({ ...s, [gallery.roomId]: index }));
  }

  return (
    <div className="bw-page" style={{ '--bw-accent': accent } as CSSProperties}>
      <div className="bw-container">
        <header className="bw-header">
          <p className="bw-kicker">{t('bookingWidget.kicker')}</p>
          <h1 className="bw-title">{data?.name ?? '\u00a0'}</h1>
        </header>

        {step !== 'success' && (
          <div className="bw-search" ref={searchRef}>
            <div className="bw-search-bar">
              <button
                type="button"
                className={'bw-field' + (pickerOpen && picking === 'in' ? ' is-active' : '')}
                onClick={() => openPicker('in')}
              >
                <span className="bw-field-label">{t('bookingWidget.checkIn')}</span>
                <span className="bw-field-value">{checkIn ? formatShort(checkIn) : t('bookingWidget.addDate')}</span>
              </button>
              <button
                type="button"
                className={'bw-field' + (pickerOpen && picking === 'out' ? ' is-active' : '')}
                onClick={() => openPicker('out')}
              >
                <span className="bw-field-label">{t('bookingWidget.checkOut')}</span>
                <span className="bw-field-value">
                  {checkOut && datesReady ? formatShort(checkOut) : t('bookingWidget.addDate')}
                </span>
              </button>
              <div className="bw-field bw-field--guests">
                <span className="bw-field-label">{t('bookingWidget.guestsLabel')}</span>
                <div className="bw-stepper">
                  <button
                    type="button"
                    className="bw-stepper-btn"
                    disabled={guests <= 1}
                    aria-label={t('bookingWidget.fewerGuests')}
                    onClick={() => setGuests((g) => Math.max(1, g - 1))}
                  >
                    −
                  </button>
                  <span className="bw-stepper-value">{guests}</span>
                  <button
                    type="button"
                    className="bw-stepper-btn"
                    disabled={guests >= maxGuests}
                    aria-label={t('bookingWidget.moreGuests')}
                    onClick={() => setGuests((g) => Math.min(maxGuests, g + 1))}
                  >
                    +
                  </button>
                </div>
              </div>
            </div>

            {pickerOpen && (
              <div className="bw-picker" role="dialog" aria-label={t('bookingWidget.chooseDates')}>
                <div className="bw-picker-head">
                  <p className="bw-picker-hint">
                    {picking === 'out'
                      ? t('bookingWidget.pickCheckout')
                      : t('bookingWidget.pickCheckin')}
                  </p>
                  <button type="button" className="bw-text-btn" onClick={clearDates}>
                    {t('bookingWidget.resetDates')}
                  </button>
                </div>
                <div className="bw-calendars">
                  <MonthGrid
                    year={pickerMonth.year}
                    month={pickerMonth.month}
                    locale={locale}
                    weekdayNames={weekdayNames}
                    today={today}
                    checkIn={checkIn}
                    checkOut={datesReady ? checkOut : null}
                    picking={picking}
                    canCheckInOn={canCheckInOn}
                    canCheckOutOn={canCheckOutOn}
                    onDayClick={handleDayClick}
                    onPrev={() => setPickerMonth((m) => shiftMonth(m.year, m.month, -1))}
                    onNext={() => setPickerMonth((m) => shiftMonth(m.year, m.month, 1))}
                    disablePrev={isCurrentPickerMonth}
                    disableNext={isLastPickerMonth}
                    showNav
                  />
                  {showSecondMonth && (
                    <MonthGrid
                      year={secondMonth.year}
                      month={secondMonth.month}
                      locale={locale}
                      weekdayNames={weekdayNames}
                      today={today}
                      checkIn={checkIn}
                      checkOut={datesReady ? checkOut : null}
                      picking={picking}
                      canCheckInOn={canCheckInOn}
                      canCheckOutOn={canCheckOutOn}
                      onDayClick={handleDayClick}
                      onPrev={() => setPickerMonth((m) => shiftMonth(m.year, m.month, -1))}
                      onNext={() => setPickerMonth((m) => shiftMonth(m.year, m.month, 1))}
                      disablePrev={isCurrentPickerMonth}
                      disableNext={isLastPickerMonth}
                      showNav={false}
                      extraClass="bw-cal--second"
                    />
                  )}
                </div>
                <div className="bw-picker-foot">
                  <p>
                    {datesReady
                      ? `${t('bookingWidget.nights', { count: stayNights })} · ${formatLong(checkIn)} → ${formatLong(checkOut)}`
                      : t('bookingWidget.pickCheckout')}
                  </p>
                  <button type="button" className="bw-btn-primary" onClick={() => setPickerOpen(false)}>
                    {t('bookingWidget.done')}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {error ? (
          <div className="bw-state">
            <p>{t('bookingWidget.loadError')}</p>
            <button type="button" className="bw-btn-primary" onClick={() => setReloadKey((k) => k + 1)}>
              {t('common.retry')}
            </button>
          </div>
        ) : loading && !data ? (
          <div className="bw-state">{t('common.loading')}</div>
        ) : data && data.rooms.length === 0 ? (
          <div className="bw-state">{t('bookingWidget.noRooms')}</div>
        ) : data && step === 'browse' ? (
          <>
            {bannerError && <div className="bw-banner bw-banner--error">{bannerError}</div>}
            <div className="bw-results-meta">
              <p>
                {datesReady
                  ? t('bookingWidget.availableCount', { count: availableRooms.length })
                  : t('bookingWidget.browseHint')}
              </p>
              {datesReady && (
                <p className="bw-results-stay">
                  {t('bookingWidget.nights', { count: stayNights })}
                  {' · '}
                  {t('bookingWidget.guests', { count: guests })}
                </p>
              )}
            </div>

            {datesReady && availableRooms.length === 0 && (
              <p className="bw-results-note">{t('bookingWidget.noMatch')}</p>
            )}
            <div className={'bw-list' + (loading ? ' is-loading' : '')}>
              {(datesReady ? [...availableRooms, ...unavailableRooms] : rooms).map((room) => {
                const available = datesReady ? roomAvailable(room) : true;
                return (
                  <RoomCard
                    key={room.id}
                    room={room}
                    photoIndex={photoIdx[room.id] ?? 0}
                    onMovePhoto={movePhoto}
                    available={available}
                    datesReady={datesReady}
                    stayNights={stayNights}
                    guests={guests}
                    formatPrice={AMD}
                    reason={!available ? (!roomFits(room) ? 'capacity' : 'dates') : undefined}
                    onReserve={() => startBooking(room)}
                    onNeedDates={() => openPicker('in')}
                    onOpenPhotos={(index) => openGallery(room.id, index)}
                  />
                );
              })}
            </div>
          </>
        ) : data && step === 'book' && selectedRoom ? (
          <section className="bw-checkout">
            <button type="button" className="bw-back" onClick={backToBrowse}>
              ← {t('bookingWidget.back')}
            </button>
            <div className="bw-checkout-grid">
              <div className="bw-summary">
                <RoomPhoto
                  room={selectedRoom}
                  index={photoIdx[selectedRoom.id] ?? 0}
                  onMove={movePhoto}
                  onOpen={(index) => openGallery(selectedRoom.id, index)}
                />
                <h2 className="bw-room-name">{selectedRoom.name}</h2>
                <p className="bw-room-meta">
                  {t('bookingWidget.sleeps', { count: selectedRoom.capacity })}
                </p>
                <dl className="bw-dl">
                  <div>
                    <dt>{t('bookingWidget.checkIn')}</dt>
                    <dd>{formatLong(checkIn)}</dd>
                  </div>
                  <div>
                    <dt>{t('bookingWidget.checkOut')}</dt>
                    <dd>{formatLong(checkOut)}</dd>
                  </div>
                  <div>
                    <dt>{t('bookingWidget.guestsLabel')}</dt>
                    <dd>{t('bookingWidget.guests', { count: guests })}</dd>
                  </div>
                </dl>
                {selectedRoom.price_amd > 0 && (
                  <div className="bw-price-box">
                    <div className="bw-price-row">
                      <span>
                        {AMD(selectedRoom.price_amd)} {t('bookingWidget.perNight')}
                        {' × '}
                        {t('bookingWidget.nights', { count: stayNights })}
                      </span>
                      <span>{AMD(stayTotal)}</span>
                    </div>
                    <div className="bw-price-row bw-price-row--total">
                      <span>{t('bookingWidget.stayTotal')}</span>
                      <strong>{AMD(stayTotal)}</strong>
                    </div>
                    <p className="bw-pay-hint">{t('bookingWidget.payOnSite')}</p>
                  </div>
                )}
              </div>

              <form
                className="bw-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  void submitBooking();
                }}
              >
                <h2 className="bw-form-title">{t('bookingWidget.guestDetails')}</h2>
                <label className="bw-label">
                  {t('bookingWidget.guestName')}
                  <input
                    className="bw-input"
                    value={guestName}
                    maxLength={120}
                    autoComplete="name"
                    required
                    onChange={(e) => setGuestName(e.target.value)}
                  />
                </label>
                <label className="bw-label">
                  {t('bookingWidget.phone')}
                  <PhoneCountryInput
                    className="bw-phone"
                    inputClassName="bw-input"
                    iso={phoneIso}
                    national={phone}
                    locale={locale}
                    onChange={({ iso, national }) => {
                      setPhoneIso(iso);
                      setPhone(national);
                    }}
                  />
                </label>
                <label className="bw-label">
                  {t('bookingWidget.email')}
                  <input
                    className="bw-input"
                    type="email"
                    autoComplete="email"
                    value={guestEmail}
                    required
                    onChange={(e) => setGuestEmail(e.target.value)}
                  />
                </label>
                {submitError && (
                  <div className="bw-banner bw-banner--error">{t('bookingWidget.bookError')}</div>
                )}
                <button
                  type="submit"
                  className="bw-btn-primary bw-btn-primary--block"
                  disabled={!formValid || submitting}
                >
                  {submitting ? '…' : t('bookingWidget.confirmBooking')}
                </button>
              </form>
            </div>
          </section>
        ) : data && step === 'success' && selectedRoom ? (
          <section className="bw-success">
            <div className="bw-success-card">
              <div className="bw-success-head">
                <div className="bw-success-mark" aria-hidden>
                  ✓
                </div>
                <div>
                  <h2 className="bw-success-title">{t('bookingWidget.bookSuccessTitle')}</h2>
                  <p className="bw-success-copy">{t('bookingWidget.bookSuccess')}</p>
                </div>
              </div>
              <dl className="bw-dl">
                <div>
                  <dt>{t('bookingWidget.colRoom')}</dt>
                  <dd>{selectedRoom.name}</dd>
                </div>
                <div>
                  <dt>{t('bookingWidget.checkIn')}</dt>
                  <dd>{formatLong(checkIn)}</dd>
                </div>
                <div>
                  <dt>{t('bookingWidget.checkOut')}</dt>
                  <dd>{formatLong(checkOut)}</dd>
                </div>
                <div>
                  <dt>{t('bookingWidget.guestsLabel')}</dt>
                  <dd>{t('bookingWidget.guests', { count: guests })}</dd>
                </div>
                {stayTotal > 0 && (
                  <div>
                    <dt>{t('bookingWidget.stayTotal')}</dt>
                    <dd>{AMD(stayTotal)}</dd>
                  </div>
                )}
              </dl>
              <button
                type="button"
                className="bw-btn-primary bw-btn-primary--block"
                onClick={bookAnother}
              >
                {t('bookingWidget.bookAnother')}
              </button>
            </div>
          </section>
        ) : null}
      </div>
      {galleryRoom && gallery && galleryRoom.photos.length > 0 && (
        <PhotoLightbox
          title={galleryRoom.name}
          photos={galleryRoom.photos}
          index={Math.min(gallery.index, galleryRoom.photos.length - 1)}
          onClose={() => setGallery(null)}
          onChange={setGalleryIndex}
        />
      )}
    </div>
  );
}

function RoomCard({
  room,
  photoIndex,
  onMovePhoto,
  available,
  datesReady,
  stayNights,
  guests,
  formatPrice,
  reason,
  onReserve,
  onNeedDates,
  onOpenPhotos,
}: {
  room: WidgetRoom;
  photoIndex: number;
  onMovePhoto: (roomId: string, count: number, delta: number) => void;
  available: boolean;
  datesReady: boolean;
  stayNights: number;
  guests: number;
  formatPrice: (n: number) => string;
  reason?: 'capacity' | 'dates';
  onReserve: () => void;
  onNeedDates: () => void;
  onOpenPhotos: (index: number) => void;
}) {
  const { t } = useTranslation();
  const total = room.price_amd > 0 && datesReady && stayNights > 0 ? room.price_amd * stayNights : 0;

  const badge = !available
    ? reason === 'capacity'
      ? t('bookingWidget.tooSmallBadge')
      : t('bookingWidget.unavailable')
    : undefined;

  return (
    <article className={'bw-card' + (!available ? ' is-unavailable' : '')}>
      <RoomPhoto
        room={room}
        index={photoIndex}
        onMove={onMovePhoto}
        badge={badge}
        onOpen={onOpenPhotos}
      />
      <div className="bw-card-body">
        <div className="bw-card-copy">
          <h2 className="bw-room-name">{room.name}</h2>
          <p className="bw-room-meta">{t('bookingWidget.sleeps', { count: room.capacity })}</p>
        </div>
        <div className="bw-card-cta">
          {room.price_amd > 0 && (
            <p className="bw-price">
              {datesReady && total > 0 ? (
                <>
                  <strong>{formatPrice(total)}</strong>
                  <span> {t('bookingWidget.forStay', { count: stayNights })}</span>
                </>
              ) : (
                <>
                  <strong>{formatPrice(room.price_amd)}</strong>
                  <span> {t('bookingWidget.perNight')}</span>
                </>
              )}
            </p>
          )}
          {available && (
            <button
              type="button"
              className="bw-btn-primary"
              onClick={datesReady ? onReserve : onNeedDates}
            >
              {datesReady ? t('bookingWidget.reserve') : t('bookingWidget.chooseDates')}
            </button>
          )}
        </div>
      </div>
    </article>
  );
}

function RoomPhoto({
  room,
  index,
  onMove,
  badge,
  onOpen,
}: {
  room: WidgetRoom;
  index: number;
  onMove: (roomId: string, count: number, delta: number) => void;
  badge?: string;
  onOpen?: (index: number) => void;
}) {
  const { t } = useTranslation();
  const photos = room.photos;
  const idx = Math.min(index, Math.max(0, photos.length - 1));
  const touch = useRef<{ x: number } | null>(null);

  if (photos.length === 0) {
    return (
      <div className="bw-photo bw-photo--empty" aria-hidden>
        {badge && <span className="bw-badge">{badge}</span>}
        <span>{room.name.slice(0, 1)}</span>
      </div>
    );
  }

  return (
    <div
      className="bw-photo"
      onTouchStart={(e) => {
        touch.current = { x: e.changedTouches[0]?.clientX ?? 0 };
      }}
      onTouchEnd={(e) => {
        if (!touch.current || photos.length < 2) return;
        const dx = (e.changedTouches[0]?.clientX ?? 0) - touch.current.x;
        if (Math.abs(dx) > 40) onMove(room.id, photos.length, dx < 0 ? 1 : -1);
        touch.current = null;
      }}
    >
      <button
        type="button"
        className="bw-photo-hit"
        onClick={() => onOpen?.(idx)}
        aria-label={t('bookingWidget.viewPhotos')}
      >
        <img src={photos[idx]} alt={room.name} loading="lazy" />
      </button>
      {badge && <span className="bw-badge">{badge}</span>}
      {onOpen && (
        <button
          type="button"
          className="bw-photo-open"
          onClick={() => onOpen(idx)}
        >
          {t('bookingWidget.viewPhotos')}
        </button>
      )}
      {photos.length > 1 && (
        <>
          <button
            type="button"
            className="bw-photo-nav bw-photo-nav--prev"
            onClick={(e) => {
              e.stopPropagation();
              onMove(room.id, photos.length, -1);
            }}
            aria-label={t('bookingWidget.prevPhoto')}
          >
            ‹
          </button>
          <button
            type="button"
            className="bw-photo-nav bw-photo-nav--next"
            onClick={(e) => {
              e.stopPropagation();
              onMove(room.id, photos.length, 1);
            }}
            aria-label={t('bookingWidget.nextPhoto')}
          >
            ›
          </button>
          <div className="bw-photo-dots">
            {photos.map((_, i) => (
              <span key={i} className={'bw-dot' + (i === idx ? ' is-active' : '')} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function PhotoLightbox({
  title,
  photos,
  index,
  onClose,
  onChange,
}: {
  title: string;
  photos: string[];
  index: number;
  onClose: () => void;
  onChange: (index: number) => void;
}) {
  const { t } = useTranslation();
  const touch = useRef<{ x: number } | null>(null);
  const count = photos.length;
  const safeIndex = ((index % count) + count) % count;

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight' && count > 1) onChange((safeIndex + 1) % count);
      if (e.key === 'ArrowLeft' && count > 1) onChange((safeIndex - 1 + count) % count);
    }
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [safeIndex, count, onClose, onChange]);

  return createPortal(
    <div className="bw-lightbox" role="dialog" aria-modal="true" aria-label={title}>
      <button type="button" className="bw-lightbox-backdrop" onClick={onClose} aria-label={t('bookingWidget.closePhotos')} />
      <div
        className="bw-lightbox-stage"
        onTouchStart={(e) => {
          touch.current = { x: e.changedTouches[0]?.clientX ?? 0 };
        }}
        onTouchEnd={(e) => {
          if (!touch.current || count < 2) return;
          const dx = (e.changedTouches[0]?.clientX ?? 0) - touch.current.x;
          if (Math.abs(dx) > 50) onChange(dx < 0 ? (safeIndex + 1) % count : (safeIndex - 1 + count) % count);
          touch.current = null;
        }}
      >
        <img src={photos[safeIndex]} alt={title} />
      </div>
      <div className="bw-lightbox-bar">
        <div>
          <p className="bw-lightbox-title">{title}</p>
          <p className="bw-lightbox-count">{t('bookingWidget.photoOf', { current: safeIndex + 1, total: count })}</p>
        </div>
        <button type="button" className="bw-lightbox-close" onClick={onClose}>
          {t('bookingWidget.closePhotos')}
        </button>
      </div>
      {count > 1 && (
        <>
          <button
            type="button"
            className="bw-lightbox-nav bw-lightbox-nav--prev"
            onClick={() => onChange((safeIndex - 1 + count) % count)}
            aria-label={t('bookingWidget.prevPhoto')}
          >
            ‹
          </button>
          <button
            type="button"
            className="bw-lightbox-nav bw-lightbox-nav--next"
            onClick={() => onChange((safeIndex + 1) % count)}
            aria-label={t('bookingWidget.nextPhoto')}
          >
            ›
          </button>
        </>
      )}
    </div>,
    document.body,
  );
}

function MonthGrid({
  year,
  month,
  locale,
  weekdayNames,
  today,
  checkIn,
  checkOut,
  picking,
  canCheckInOn,
  canCheckOutOn,
  onDayClick,
  onPrev,
  onNext,
  disablePrev,
  disableNext,
  showNav,
  extraClass,
}: {
  year: number;
  month: number;
  locale: string;
  weekdayNames: string[];
  today: string;
  checkIn: string | null;
  checkOut: string | null;
  picking: 'in' | 'out';
  canCheckInOn: (iso: string) => boolean;
  canCheckOutOn: (iso: string) => boolean;
  onDayClick: (iso: string) => void;
  onPrev: () => void;
  onNext: () => void;
  disablePrev: boolean;
  disableNext: boolean;
  showNav: boolean;
  extraClass?: string;
}) {
  const { t } = useTranslation();
  const start = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const leadingBlanks = (start.getDay() + 6) % 7;
  const label = start.toLocaleDateString(locale, { month: 'long', year: 'numeric' });

  return (
    <div className={'bw-cal' + (extraClass ? ` ${extraClass}` : '')}>
      <div className="bw-cal-nav">
        {showNav ? (
          <button
            type="button"
            className="bw-nav-btn"
            onClick={onPrev}
            disabled={disablePrev}
            aria-label={t('bookingWidget.prevMonth')}
          >
            ‹
          </button>
        ) : (
          <span />
        )}
        <span className="bw-cal-label">{label}</span>
        {showNav ? (
          <button
            type="button"
            className="bw-nav-btn"
            onClick={onNext}
            disabled={disableNext}
            aria-label={t('bookingWidget.nextMonth')}
          >
            ›
          </button>
        ) : (
          <span />
        )}
      </div>
      <div className="bw-cal-grid">
        {weekdayNames.map((w, i) => (
          <span key={'w' + i} className="bw-cal-dow">
            {w}
          </span>
        ))}
        {Array.from({ length: leadingBlanks }, (_, i) => (
          <span key={'b' + i} />
        ))}
        {Array.from({ length: daysInMonth }, (_, i) => {
          const day = i + 1;
          const iso = isoDate(new Date(year, month, day));
          const isPast = iso < today;
          const isStart = checkIn === iso;
          const isEnd = checkOut === iso;
          const inRange = !!checkIn && !!checkOut && iso > checkIn && iso < checkOut;
          const clickable =
            !isPast && (picking === 'out' && checkIn ? canCheckOutOn(iso) : canCheckInOn(iso));
          return (
            <button
              type="button"
              key={day}
              disabled={!clickable}
              onClick={() => onDayClick(iso)}
              className={
                'bw-cal-day' +
                (isPast ? ' is-past' : '') +
                (!isPast && !clickable ? ' is-blocked' : '') +
                (iso === today ? ' is-today' : '') +
                (clickable ? ' is-clickable' : '') +
                (isStart || isEnd ? ' is-edge' : '') +
                (inRange ? ' is-range' : '')
              }
            >
              {day}
            </button>
          );
        })}
      </div>
    </div>
  );
}
